<script lang="ts">
import { defineComponent, provide, readonly, toRef, ref, reactive, nextTick, onBeforeUnmount } from 'vue'
import ToastMessageForLocalData from '@/components/toast/ToastMessageForLocalData.vue'
import { VALIDATION_OBSERVER_KEY } from '@/components/validation/context'
import { validationInspectorRegistry } from '@/components/validation/inspectorRegistry'
import { useErrorStore } from '@/stores/error'
import { useHeaderStore } from '@/stores/header'
import type { ValidationFieldHandle } from '@/components/validation/context'
import type { ValidationInspectorEntry } from '@/components/validation/inspectorRegistry'

export default defineComponent({
  name: 'ValidationObserver',

  components: {
    ToastMessageForLocalData,
  },

  props: {
    /**
     * バリデーションの名前空間
     * @description 配下の ValidationProvider のうち、同じ namespace を持つものだけを検査対象とする。
     * @description Form submit の場合、form の name 属性の値にする。
     */
    namespace: {
      type: String,
      default: '',
    },

    /**
     * バリデーションエラー発生時にエラー箇所までスクロールするかどうか
     * @description デフォルトは true であるが、モーダル内でバリデーションを行う際のように、
     *              背景レイヤーが動いてしまうことを避けたい場合はこの値を false にする。
     */
    scrollByValidationError: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['error', 'valid', 'completed'],

  setup(props, { emit }) {
    const errorStore = useErrorStore()
    const headerStore = useHeaderStore()

    const rootEl = ref<HTMLElement>()
    const toastEl = ref()
    const inProgress = ref(false)

    /**
     * 配下の ValidationProvider から登録された検査ハンドル
     */
    const handles = new Set<ValidationFieldHandle>()

    /**
     * ValidationInspector（開発時のデバッグ表示）向けの検査状況
     */
    const inspectorEntry: ValidationInspectorEntry = reactive({
      get namespace() {
        return props.namespace
      },
      fields: [],
      checkedFields: [],
      inProgress: false,
    })

    const syncInspectorFields = () => {
      inspectorEntry.fields = [...handles].map(handle => handle.field)
    }

    provide(VALIDATION_OBSERVER_KEY, {
      namespace: toRef(props, 'namespace'),
      inProgress: readonly(inProgress),
      register: (handle) => {
        handles.add(handle)
        syncInspectorFields()
      },
      unregister: (handle) => {
        handles.delete(handle)
        syncInspectorFields()
      },
    })

    validationInspectorRegistry.add(inspectorEntry)
    onBeforeUnmount(() => {
      validationInspectorRegistry.remove(inspectorEntry)
    })

    /**
     * Provider 1 件の検査
     * @description 検査処理が例外を投げても全体を止めず、その項目を不合格として扱う（透明レイヤーが外れなくなる事故を防ぐ）
     */
    const validateHandle = async (handle: ValidationFieldHandle): Promise<boolean> => {
      try {
        return await handle.validate()
      } catch (error) {
        console.error(`[ValidationObserver] "${handle.field}" の検査中に例外が発生しました`, error)
        return false
      } finally {
        inspectorEntry.checkedFields.push(handle.field)
      }
    }

    const run = async (): Promise<boolean> => {
      inProgress.value = true
      inspectorEntry.inProgress = true
      inspectorEntry.checkedFields = []

      try {
        // 前回のエラー表示（この区画のものと、区画に属さないサーバ由来のもの）を消してから検査する。
        // 検査対象の確定は消去待ちの後に行い、その間にマウント／アンマウントされた Provider を取りこぼさない
        await errorStore.clearErrors({ namespace: props.namespace })

        const results = await Promise.all([...handles].map(validateHandle))
        const isValid = results.every(result => result)

        if (isValid) {
          emit('valid', props.namespace)
        } else {
          emit('error', props.namespace)

          toastEl.value?.setToastItem({
            id: `validation-error-${new Date().getTime()}`,
            messageType: 'alert',
            message: '入力内容に不備があります',
            duration: 5,
          })

          scrollToFieldByValidationError()
        }

        return isValid
      } finally {
        inProgress.value = false
        inspectorEntry.inProgress = false
        emit('completed', props.namespace)
      }
    }

    let pending: Promise<boolean> | undefined

    /**
     * 配下の ValidationProvider 全件を検査し、すべて通過したかどうかを返す
     * @description 結果に応じて 'valid' か 'error' を、その後に 'completed' を通知する。登録が 0 件なら即 true。
     * @description 検査中に再度呼ばれた場合（非同期チェック待ちの間の Enter キー再送信等）は、進行中の検査の結果をそのまま返す。
     */
    const validate = (): Promise<boolean> => {
      if (!pending) {
        pending = run().finally(() => {
          pending = undefined
        })
      }
      return pending
    }

    /**
     * バリデーションエラー発生時に、この区画内の最初のエラー箇所までスクロール
     * （ウィンドウのスクロールのため、要素内スクロールには非対応）
     */
    const scrollToFieldByValidationError = (): void => {
      if (!props.scrollByValidationError) return

      // DOM の再レンダリングを待機してからエラー要素を拾って対象までスクロール
      nextTick(() => {
        const firstErrorDOM = rootEl.value?.querySelector('[data-validation-error="true"]')
        if (!firstErrorDOM) return

        const domPosition = firstErrorDOM.getBoundingClientRect().top + window.scrollY

        // 下スクロールだとヘッダーが消えるのでヘッダー分は加味しない
        const offset = -(domPosition < window.scrollY ? headerStore.context.height : 0)

        // Vue ScrollTo だと上手くスクロール発火できないため、window の scrollTo イベントで対応
        window.scrollTo({
          top: domPosition + offset,
          behavior: 'smooth'
        })
      })
    }

    return {
      // data
      rootEl,
      toastEl,
      inProgress,

      // methods
      validate,
    }
  }
})
</script>

<template>
  <section
    ref="rootEl"
    class="f-section__validationObserver"
    :data-progress="inProgress">
    <div class="f-section__validationObserver--body">
      <slot />
      <ToastMessageForLocalData ref="toastEl" />
    <!-- /.f-section__validationObserver body --></div>
  <!-- /.f-section__validationObserver --></section>
</template>

<style lang="sass" scoped>
.f-section__validationObserver
  $root: &

  // 検査中は透明なレイヤーで画面全体の操作を止める
  &[data-progress="true"]
    #{$root}--body
      &::before
        content: ''
        width: 100%
        height: 100%
        display: block
        position: fixed
        top: 0
        left: 0
        z-index: 20 // same value as screen lock in Template components z-index
</style>
